import { index, sqliteTable, text, real, integer } from "drizzle-orm/sqlite-core";

// ============================================================================
// period_closes — Inventory month close status (#497)
// ============================================================================
export const inventoryPeriodCloses = sqliteTable("inventory_period_closes", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  periodMonth: text("period_month").notNull(), // YYYY-MM
  status: text("status", { enum: ["open", "closed"] }).notNull().default("closed"),
  reason: text("reason"),
  valuationDelta: real("valuation_delta").notNull().default(0),
  glInventoryDelta: real("gl_inventory_delta").notNull().default(0),
  glCogsDelta: real("gl_cogs_delta").notNull().default(0),
  varianceAmount: real("variance_amount").notNull().default(0),
  exceptionCount: integer("exception_count").notNull().default(0),
  closedAt: text("closed_at"),
  closedBy: text("closed_by"),
  reopenedAt: text("reopened_at"),
  reopenedBy: text("reopened_by"),
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  updatedAt: text("updated_at").notNull(),
  updatedBy: text("updated_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => ({
  ipcCompanyPeriodIdx: index("ipc_company_period_idx").on(table.companyId, table.periodMonth),
  ipcCompanyStatusIdx: index("ipc_company_status_idx").on(table.companyId, table.status),
}));

export type InventoryPeriodClose = typeof inventoryPeriodCloses.$inferSelect;
export type NewInventoryPeriodClose = typeof inventoryPeriodCloses.$inferInsert;

// ============================================================================
// close_exceptions — Operator queue for valuation / close anomalies (#497)
// ============================================================================
export const inventoryCloseExceptions = sqliteTable("inventory_close_exceptions", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  periodMonth: text("period_month").notNull(), // YYYY-MM
  exceptionKey: text("exception_key").notNull(),
  exceptionType: text("exception_type", {
    enum: [
      "zero_cogs",
      "negative_stock",
      "missing_valuation_rate",
      "missing_inventory_gl",
      "missing_account",
      "side_effect_failed",
    ],
  }).notNull(),
  severity: text("severity", { enum: ["info", "warning", "critical"] }).notNull().default("warning"),
  status: text("status", { enum: ["open", "resolved"] }).notNull().default("open"),
  sourceModule: text("source_module"),
  sourceDoctype: text("source_doctype"),
  sourceId: text("source_id"),
  stockEntryId: text("stock_entry_id"),
  itemId: text("item_id"),
  warehouseId: text("warehouse_id"),
  postingDate: text("posting_date"),
  message: text("message").notNull(),
  details: text("details"),
  retryCount: integer("retry_count").notNull().default(0),
  lastRetryAt: text("last_retry_at"),
  resolvedAt: text("resolved_at"),
  resolvedBy: text("resolved_by"),
  resolutionNote: text("resolution_note"),
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  updatedAt: text("updated_at").notNull(),
  updatedBy: text("updated_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => ({
  iceCompanyPeriodStatusIdx: index("ice_company_period_status_idx").on(
    table.companyId,
    table.periodMonth,
    table.status,
  ),
  iceCompanyKeyIdx: index("ice_company_key_idx").on(table.companyId, table.exceptionKey),
  iceStockEntryIdx: index("ice_stock_entry_idx").on(table.companyId, table.stockEntryId),
}));

export type InventoryCloseException = typeof inventoryCloseExceptions.$inferSelect;
export type NewInventoryCloseException = typeof inventoryCloseExceptions.$inferInsert;
